import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fetchJob, retryJob, discardJob } from '../lib/api';
import type { JobSummary } from '../lib/api';
import { dateFnsLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { opsErrorMessage } from '../lib/ops-api';
import { AuditRecordedNote, useAuditReasonConfirm } from './AuditReasonConfirm';
import { Icon } from './Brand';
import { StatusBanner } from './StatusBanner';

interface Props {
  queueName: string;
  job: JobSummary;
  onClose: () => void;
}

export function FailedJobDrawer({ queueName, job, onClose }: Props) {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  // Retry and discard write an OperatorAuditLog row with a typed reason (AC-G4c).
  const { ask, dialog } = useAuditReasonConfirm();
  const [activeTab, setActiveTab] = useState<'stacktrace' | 'data' | 'opts'>(
    'stacktrace',
  );

  const { data: detail, isLoading } = useQuery({
    queryKey: ['job', queueName, job.id],
    queryFn: () => fetchJob(queueName, job.id),
  });

  // The drawer stays open after a success — it used to close — so the operator sees the audit
  // row the action wrote. The buttons then stay disabled: the job has moved on.
  const retryMutation = useMutation({
    mutationFn: (reason: string) => retryJob(queueName, job.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const discardMutation = useMutation({
    mutationFn: (reason: string) => discardJob(queueName, job.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const done = retryMutation.isSuccess || discardMutation.isSuccess;
  const busy = retryMutation.isPending || discardMutation.isPending;

  /** Payload panes. Monospace on a tinted surface — the one place dark type on grey is right. */
  const preClass =
    'text-xs text-gray-800 font-mono whitespace-pre-wrap break-words bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-auto';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/40 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer - full width on mobile, max 2xl on desktop */}
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-2xl bg-white border-l border-gray-200 z-50 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 bg-gradient-to-b from-primary-50/60 to-white">
          <div className="min-w-0">
            <p className="font-mono text-xs text-gray-500 mb-1">
              {queueName} / #{job.id}
            </p>
            <h2 className="text-lg font-bold text-gray-900 truncate">
              {job.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 flex-shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
            aria-label={t('common.close')}
          >
            <Icon name="close" className="h-5 w-5" />
          </button>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 sm:px-6 py-4 border-b border-gray-200 text-sm">
          <div>
            <p className="text-gray-500 text-xs mb-1">
              {t('jobs.drawer.failedAt')}
            </p>
            <p className="text-gray-900">
              {job.timestamp
                ? format(new Date(job.timestamp), 'dd MMM yyyy HH:mm:ss', {
                    // The pattern stays numeric-day/short-month either way; the locale is what
                    // turns `Mar` into `Mär`.
                    locale: dateFnsLocale(locale),
                  })
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">
              {t('jobs.drawer.attempts')}
            </p>
            <p className="text-gray-900 tabular-nums">
              {job.attemptsMade} / {job.maxAttempts}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">
              {t('jobs.drawer.priority')}
            </p>
            <p className="text-gray-900">{job.priority ?? '—'}</p>
          </div>
        </div>

        {/* Error summary */}
        {job.failedReason && (
          <div className="mx-4 sm:mx-6 mt-4">
            <StatusBanner tone="error">
              <p className="font-medium mb-1">{t('jobs.drawer.error')}</p>
              <p className="font-mono break-words">{job.failedReason}</p>
            </StatusBanner>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex gap-4 px-4 sm:px-6 mt-4 border-b border-gray-200 overflow-x-auto">
          {(['stacktrace', 'data', 'opts'] as const).map((tab) => (
            <button
              type="button"
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors min-h-[44px] flex items-end ${
                activeTab === tab
                  ? 'border-primary text-primary-800'
                  : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              {t(
                tab === 'stacktrace'
                  ? 'jobs.drawer.tabStacktrace'
                  : tab === 'data'
                    ? 'jobs.drawer.tabData'
                    : 'jobs.drawer.tabOpts',
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 py-4">
          {isLoading ? (
            <div className="text-gray-500 text-sm">{t('common.loading')}</div>
          ) : detail ? (
            <>
              {activeTab === 'stacktrace' && (
                <pre className={preClass}>
                  {detail.stacktrace?.join('\n\n') || t('jobs.drawer.noStack')}
                </pre>
              )}
              {activeTab === 'data' && (
                <pre className={preClass}>
                  {JSON.stringify(detail.data, null, 2)}
                </pre>
              )}
              {activeTab === 'opts' && (
                <pre className={preClass}>
                  {JSON.stringify(detail.opts, null, 2)}
                </pre>
              )}
            </>
          ) : null}
        </div>

        {/* Action buttons */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() =>
                ask({
                  title: t('reasonPrompt.queue.retryJob.title'),
                  confirmLabel: t('reasonPrompt.queue.retryJob.confirm'),
                  body: t('reasonPrompt.queue.retryJob.body', {
                    job: `${job.name} #${job.id}`,
                    queue: queueName,
                  }),
                  onConfirm: (reason) => retryMutation.mutate(reason),
                })
              }
              disabled={busy || done}
              className="btn-primary btn-sm flex-1 gap-2"
            >
              <Icon name="refresh" className="h-4 w-4" />
              {retryMutation.isPending
                ? t('jobs.retrying')
                : t('jobs.drawer.retry')}
            </button>
            {/* Discarding is the irreversible one, so it is the one wearing red. */}
            <button
              type="button"
              onClick={() =>
                ask({
                  title: t('reasonPrompt.queue.discardJob.title'),
                  confirmLabel: t('reasonPrompt.queue.discardJob.confirm'),
                  body: t('reasonPrompt.queue.discardJob.body', {
                    job: `${job.name} #${job.id}`,
                    queue: queueName,
                  }),
                  onConfirm: (reason) => discardMutation.mutate(reason),
                })
              }
              disabled={busy || done}
              className="btn-danger btn-sm flex-1 gap-2"
            >
              <Icon name="trash" className="h-4 w-4" />
              {discardMutation.isPending
                ? t('jobs.discarding')
                : t('jobs.drawer.discard')}
            </button>
          </div>
          {retryMutation.isSuccess && (
            <StatusBanner tone="success">
              {t('reasonPrompt.queue.retryJob.done', { job: `#${job.id}` })}
              <AuditRecordedNote auditLogId={retryMutation.data.auditLogId} />
            </StatusBanner>
          )}
          {discardMutation.isSuccess && (
            <StatusBanner tone="success">
              {t('reasonPrompt.queue.discardJob.done', { job: `#${job.id}` })}
              <AuditRecordedNote auditLogId={discardMutation.data.auditLogId} />
            </StatusBanner>
          )}
          {(retryMutation.isError || discardMutation.isError) && (
            <StatusBanner tone="error">
              {opsErrorMessage(
                retryMutation.error ?? discardMutation.error,
                t('reasonPrompt.failed'),
              )}
            </StatusBanner>
          )}
        </div>
      </div>

      {dialog}
    </>
  );
}
